import { useEffect, useState, useCallback } from "react";
import { Star, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const Stars = ({ value, onPick, tid }) => (
  <div className="flex gap-1" data-testid={tid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onPick} data-testid={onPick ? `${tid}-${n}` : undefined} onClick={() => onPick?.(n)} aria-label={`${n} stars`}>
        <Star className={`w-5 h-5 ${n <= value ? "fill-amber-300 text-amber-300" : "text-slate-600"}`} />
      </button>
    ))}
  </div>
);

const ReviewForm = ({ t, onDone }) => {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const send = async () => {
    try { await api.post("/me/reviews", { award_id: t.award_id, rating, text }); toast.success("Thanks! Your review will appear once approved."); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-3 space-y-3">
      <Stars value={rating} onPick={setRating} tid={`review-stars-${t.award_id}`} />
      <textarea data-testid={`review-text-${t.award_id}`} rows={3} maxLength={600} value={text} onChange={(e) => setText(e.target.value)} className="lux-input" placeholder="How was the trip? (10–600 characters)" />
      <button data-testid={`review-submit-${t.award_id}`} disabled={text.trim().length < 10} onClick={send} className="btn-gold px-5 py-2 text-sm">Submit review</button>
    </div>
  );
};

export const ReviewsCard = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/me/reviewable").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!items.length) return null;
  return (
    <div className="lux-card p-6 mt-8" data-testid="reviews-card">
      <p className="eyebrow"><Star className="w-3.5 h-3.5 mr-1.5" />Review your trips</p>
      {items.map((t) => (
        <div key={t.award_id} className="mt-5 border-t border-white/5 pt-4" data-testid={`reviewable-${t.award_id}`}>
          <p className="text-sm">{t.vacation_title}</p>
          {t.review_status && <p className="text-xs text-slate-500 mt-1" data-testid={`review-status-${t.award_id}`}>Review {t.review_status === "pending" ? "waiting for approval" : t.review_status}</p>}
          {!t.review_status && !t.can_review && <p className="text-xs text-slate-500 mt-1">You can review after you travel{t.travel_date ? ` (${t.travel_date})` : ""}.</p>}
          {t.can_review && <ReviewForm t={t} onDone={load} />}
        </div>
      ))}
    </div>
  );
};

export const EmailPrefsCard = () => {
  const { user, setUser } = useAuth();
  const toggle = async (e) => {
    try { const { data } = await api.put("/me/email-prefs", { impact_emails: e.target.checked }); setUser({ ...user, impact_emails: data.impact_emails }); toast.success(data.impact_emails ? "You'll get a monthly update" : "Monthly updates off"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="lux-card p-6 mt-8" data-testid="email-prefs-card">
      <p className="eyebrow"><Mail className="w-3.5 h-3.5 mr-1.5" />Email updates</p>
      <label className="mt-4 flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
        <input data-testid="impact-emails-toggle" type="checkbox" checked={!!user?.impact_emails} onChange={toggle} className="accent-[#38BDF8] w-4 h-4 mt-0.5" />
        Send me a monthly update on trips awarded, scholarships funded and my spots in line.
      </label>
      <p className="text-xs text-slate-500 mt-2">Trip offers, reminders and payment alerts are always sent.</p>
    </div>
  );
};
